import clsx from "clsx";

import {
  TicketAiCategory,
  TicketAiSentiment,
} from "../../../models/Tickets/TicketInsight";
import { pillBaseClasses } from "../ticketMeta";

export const categoryLabels: Record<TicketAiCategory, string> = {
  [TicketAiCategory.Bug]: "Bug",
  [TicketAiCategory.FeatureRequest]: "Feature request",
  [TicketAiCategory.Question]: "Question",
  [TicketAiCategory.Integration]: "Integration",
  [TicketAiCategory.Account]: "Account",
  [TicketAiCategory.Billing]: "Billing",
  [TicketAiCategory.Other]: "Other",
};

export const categoryOrder: TicketAiCategory[] = [
  TicketAiCategory.Bug,
  TicketAiCategory.FeatureRequest,
  TicketAiCategory.Question,
  TicketAiCategory.Integration,
  TicketAiCategory.Account,
  TicketAiCategory.Billing,
  TicketAiCategory.Other,
];

/** Pill + bar colours. Bug/Feature match the manual type pills so the eye carries across pages. */
export const categoryPillClassMap: Record<TicketAiCategory, string> = {
  [TicketAiCategory.Bug]: "bg-rose-100 text-rose-700",
  [TicketAiCategory.FeatureRequest]: "bg-indigo-100 text-indigo-700",
  [TicketAiCategory.Question]: "bg-sky-100 text-sky-700",
  [TicketAiCategory.Integration]: "bg-amber-100 text-amber-800",
  [TicketAiCategory.Account]: "bg-violet-100 text-violet-700",
  [TicketAiCategory.Billing]: "bg-emerald-100 text-emerald-700",
  [TicketAiCategory.Other]: "bg-gray-200 text-gray-700",
};

export const categoryBarClassMap: Record<TicketAiCategory, string> = {
  [TicketAiCategory.Bug]: "bg-rose-400",
  [TicketAiCategory.FeatureRequest]: "bg-indigo-400",
  [TicketAiCategory.Question]: "bg-sky-400",
  [TicketAiCategory.Integration]: "bg-amber-400",
  [TicketAiCategory.Account]: "bg-violet-400",
  [TicketAiCategory.Billing]: "bg-emerald-400",
  [TicketAiCategory.Other]: "bg-gray-400",
};

/** The API serialises dictionary keys as enum names, so byCategory maps need a name -> enum lookup. */
export const categoryByName: Record<string, TicketAiCategory> = {
  Bug: TicketAiCategory.Bug,
  FeatureRequest: TicketAiCategory.FeatureRequest,
  Question: TicketAiCategory.Question,
  Integration: TicketAiCategory.Integration,
  Account: TicketAiCategory.Account,
  Billing: TicketAiCategory.Billing,
  Other: TicketAiCategory.Other,
};

/** Enum -> API name, for reading byCategory dictionaries in enum order. */
export const categoryNames: Record<TicketAiCategory, string> = {
  [TicketAiCategory.Bug]: "Bug",
  [TicketAiCategory.FeatureRequest]: "FeatureRequest",
  [TicketAiCategory.Question]: "Question",
  [TicketAiCategory.Integration]: "Integration",
  [TicketAiCategory.Account]: "Account",
  [TicketAiCategory.Billing]: "Billing",
  [TicketAiCategory.Other]: "Other",
};

export const sentimentLabels: Record<TicketAiSentiment, string> = {
  [TicketAiSentiment.Negative]: "Negative",
  [TicketAiSentiment.Neutral]: "Neutral",
  [TicketAiSentiment.Positive]: "Positive",
};

export const sentimentPillClassMap: Record<TicketAiSentiment, string> = {
  [TicketAiSentiment.Negative]: "bg-rose-100 text-rose-700",
  [TicketAiSentiment.Neutral]: "bg-gray-100 text-gray-700",
  [TicketAiSentiment.Positive]: "bg-green-100 text-green-700",
};

export const sentimentBarClassMap: Record<TicketAiSentiment, string> = {
  [TicketAiSentiment.Negative]: "bg-rose-400",
  [TicketAiSentiment.Neutral]: "bg-gray-300",
  [TicketAiSentiment.Positive]: "bg-green-400",
};

export const urgencyBarClassMap: Record<number, string> = {
  1: "bg-slate-300",
  2: "bg-sky-300",
  3: "bg-amber-300",
  4: "bg-orange-400",
  5: "bg-red-500",
};

export const urgencyLabels: Record<number, string> = {
  1: "Can wait",
  2: "Minor",
  3: "Hampers work",
  4: "Blocked",
  5: "Business-stopping",
};

export function CategoryPill({
  category,
  className,
}: {
  category: TicketAiCategory;
  className?: string;
}) {
  return (
    <span
      className={clsx(pillBaseClasses, categoryPillClassMap[category], className)}
    >
      {categoryLabels[category]}
    </span>
  );
}

export function SentimentPill({
  sentiment,
  className,
}: {
  sentiment: TicketAiSentiment;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        pillBaseClasses,
        sentimentPillClassMap[sentiment],
        className,
      )}
    >
      {sentimentLabels[sentiment]}
    </span>
  );
}

/** Five small blocks, filled to the urgency level, coloured by how bad it is. */
export function UrgencyMeter({ urgency }: { urgency: number }) {
  return (
    <span
      className="inline-flex items-center gap-0.5"
      title={`Urgency ${urgency}/5 - ${urgencyLabels[urgency] ?? ""}`}
    >
      {[1, 2, 3, 4, 5].map((level) => (
        <span
          key={level}
          className={clsx(
            "h-3 w-1.5 rounded-sm",
            level <= urgency ? urgencyBarClassMap[urgency] : "bg-gray-200",
          )}
        />
      ))}
    </span>
  );
}

export function formatPercent(value: number) {
  return `${Math.round(value)}%`;
}

export function formatSimilarity(value: number) {
  return `${Math.round(value * 100)}%`;
}
